import { detectMode, getChromeVariant, isVtestMode } from './lib/mode'
import { zoneForPath, wpIndexForZone } from './lib/analytics/interaction-manifest'
import { isStaticPage } from './lib/seo/seo.config'
import { initWebVitals } from './lib/analytics/web-vitals'
import { initAnalytics, track } from './lib/analytics/analytics'
import { mountConsentUI } from './lib/analytics/consent'

function main(): void {
  const host = document.getElementById('app')
  if (!host) throw new Error('main: #app host element not found')

  const guided = detectMode(location.search, window.innerWidth) === 'guided'
  initAnalytics(guided ? 'linear' : 'canvas')
  initWebVitals()
  mountConsentUI()

  // Static pages (/faq, /privacy) ARE their prerendered content — no app
  // boot; only the consent UI (above) runs so choices can be made/revoked there.
  //
  // 404 is the same, but it cannot be recognised by pathname: Cloudflare Pages serves
  // 404.html AT THE REQUESTED URL, so location.pathname is the dead link, never
  // '/404'. The prerendered [data-ssg] marker is the only reliable signal. Without
  // this the app boots straight over "Page not found" — guided replaces it with all
  // 11 home sections, desktop mounts the whole canvas — which is the soft 404 that
  // 0ea487e removed at the HTTP layer reappearing at the render layer.
  if (isStaticPage(location.pathname) || host.dataset.ssg === '/404') return

  // Prerendered zone routes (scripts/prerender.mjs) carry their static linear content
  // inside #app; the client upgrades it: guided mode swaps it for the live tree, canvas
  // mode overlays and then removes it. Unknown paths behave exactly as before (SPA).
  const zone = zoneForPath(location.pathname)
  if (zone && zone.route !== '/') track('deep_link_open', { zone_id: zone.id })

  if (guided) {
    import('./mobile/boot').then(async (m) => {
      m.boot(host, {
        chromeVariant: getChromeVariant(location.search),
        vtest: isVtestMode(location.search),
        initialAnchor: zone && zone.id !== 'home' ? zone.linearSectionId : undefined,
      })
      const { startLinearObservability } = await import('./lib/analytics/linear-observability')
      const { bindLinearTelemetry } = await import('./lib/analytics/feature-telemetry')
      startLinearObservability()
      bindLinearTelemetry()
    })
    return
  }

  // Desktop: the canvas engine is deliberately NOT in the entry chunk — the prerendered
  // static content paints first (LCP = hero text), then the canvas loads and takes over
  // after first paint (spec §1 perf budgets: dynamic import + requestIdleCallback fallback).
  const bootCanvas = (): void => {
    void import('./boot').then(async (m) => {
      const canvas = m.boot(host)
      // Static-first, upgrade-after: drop the prerendered content once the canvas
      // (position:fixed overlay) is mounted and interactive.
      //
      // The canvas is an opaque fixed overlay, so it already covers the shell the
      // moment it mounts — nothing shifts. Fading it in over the shell for one
      // short beat turns the swap into a transition instead of a cut. The shell is
      // removed on a timer rather than on animationend, because reduced motion and
      // ?vtest=1 suppress the animation entirely and the event would never arrive.
      const shell = host.querySelector('[data-ssg-shell]')
      // Guided's document-level scrolling has to stop the instant the canvas covers the
      // viewport, not when the shell is finally removed — see proto.css. Without it the
      // scrollbar outlives the fade and the canvas resizes under the reader.
      document.documentElement.dataset.canvasMounted = 'true'
      const dropShell = (): void => {
        shell?.remove()
        if (document.documentElement.dataset.mode === 'guided') delete document.documentElement.dataset.mode
        delete document.documentElement.dataset.canvasMounted
      }
      const canvasRoot = host.querySelector('[data-ref="container"]')
      if (shell && canvasRoot && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        canvasRoot.classList.add('canvas-mount-fade')
        setTimeout(dropShell, 220)
      } else {
        // No prerendered shell (dev server, tests) or reduced motion: unchanged,
        // same-tick removal.
        dropShell()
      }
      // Deep link: land framed on the route's region (instant, before the next paint).
      if (zone && zone.id !== 'home') {
        const { setNavSource } = await import('./lib/analytics/nav-source')
        setNavSource('deeplink')
        canvas.waypoints.flyToWaypoint(wpIndexForZone(zone.id), 0)
      }
      const { startCanvasObservability } = await import('./lib/analytics/canvas-observability')
      const { bindCanvasTelemetry } = await import('./lib/analytics/feature-telemetry')
      startCanvasObservability(canvas)
      bindCanvasTelemetry(canvas, { loadMs: performance.now() })
    })
  }
  if ('requestIdleCallback' in window) requestIdleCallback(bootCanvas, { timeout: 400 })
  else setTimeout(bootCanvas, 1)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main)
} else {
  main()
}
// Deliberately expose nothing on window (window.__shinro dropped, spec §6).
